import type { AdminOrgDto } from '@carcheck/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { companyErrorKey } from './companyErrors';
import { useAdminOrgsCache } from '../../../api/admin-orgs';
import { publishToast } from '../../../components/feedback';

/**
 * One runner for every write on the company card (AUT-403): edit, status,
 * member remove, member-code rotate, logo. Busy flag; on success the
 * returned company (when the write answers one) patches the card in place,
 * the `['admin','orgs']` prefix is invalidated and a success toast shows;
 * on failure exactly one toast with the mapped error code (the writes opt
 * out of the shared auto-toast) and the cache re-syncs.
 */
export interface UseOrgWriteResult {
  busy: boolean;
  /** Resolves true on success. `errorKeyOf` overrides the error mapping. */
  run: (
    write: () => Promise<unknown>,
    successKey: string,
    errorKeyOf?: (err: unknown) => string,
  ) => Promise<boolean>;
}

const isOrg = (value: unknown): value is AdminOrgDto =>
  typeof value === 'object' && value !== null && 'maxMembers' in value && 'status' in value;

export const useOrgWrite = (): UseOrgWriteResult => {
  const { t } = useTranslation();
  const cache = useAdminOrgsCache();
  const [busy, setBusy] = useState(false);

  const run: UseOrgWriteResult['run'] = async (write, successKey, errorKeyOf) => {
    setBusy(true);
    try {
      const result = await write();
      if (isOrg(result)) cache.patchDetail(result);
      await cache.invalidate();
      publishToast({ severity: 'success', durationMs: 4000, message: t(successKey) });
      return true;
    } catch (err: unknown) {
      await cache.invalidate();
      const key = (errorKeyOf ?? companyErrorKey)(err);
      publishToast({ severity: 'error', durationMs: 6000, message: t(key) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
